import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalStorageManager } from '@examdojo/browser/local-storage';
import { environment } from '@examdojo/core/environment';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { InternationalizationService } from '@examdojo/core/i18n';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { mapToVoid, shareOneReplay } from '@examdojo/rxjs';
import { ToastService } from '@examdojo/toast';
import { compareSemVer, isSemver } from '@examdojo/util/compare-semver';
import { combineLatest, filter, first, map, merge, Observable, take, tap } from 'rxjs';

@Injectable()
export class ForceUpdateAppEffectService {
  constructor(
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
    private readonly internationalizationService: InternationalizationService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    merge(this.promptToUpdateWhenBelowMinimumVersion(), this.clearNeedsUpdateFlagWhenUpToDate())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private readonly appVersionStorageManager = new LocalStorageManager<{
    needs_update: boolean;
  }>('app_version');

  private readonly minimumRequiredVersion$ = this.featureFlagService.select(
    ExamDojoFeatureFlag.MinimumRequiredVersion,
  ) as Observable<string>;

  private readonly currentVersion = environment.release_version;

  private readonly isBelowMinimumRequiredVersion$ = this.minimumRequiredVersion$.pipe(
    map((minVersion) => {
      const currentVersion = this.currentVersion;
      if (!(isSemver(minVersion) && isSemver(currentVersion))) {
        return false;
      }
      return compareSemVer(currentVersion, minVersion) < 0;
    }),
    shareOneReplay(),
  );

  private readonly message$: Observable<string> = this.internationalizationService
    .selectTranslate('update_app_prompt')
    .pipe(first(Boolean));

  private promptToUpdateWhenBelowMinimumVersion() {
    return combineLatest([this.isBelowMinimumRequiredVersion$, this.message$]).pipe(
      filter(([isBelowMinimumRequiredVersion]) => isBelowMinimumRequiredVersion),
      tap(([_, message]) => {
        if (this.appVersionStorageManager.getValue()?.needs_update) {
          // Prevent getting stuck in an alert loop
          this.errorHandlerService.error(
            `[ForceUpdateAppEffectService]: Cannot update the app - stuck with version ${this.currentVersion}`,
          );
          this.toastService.error(message, { dismissible: false, autoClose: false });
          return;
        }

        confirm(message);
        this.appVersionStorageManager.setValue({ needs_update: true });
        this.document.location.reload();
      }),
      mapToVoid(),
    );
  }

  private clearNeedsUpdateFlagWhenUpToDate() {
    return this.isBelowMinimumRequiredVersion$.pipe(
      filter((isBelowMinimumRequiredVersion) => !isBelowMinimumRequiredVersion),
      tap(() => {
        if (this.appVersionStorageManager.getValue()?.needs_update) {
          this.appVersionStorageManager.setValue({ needs_update: false });
        }
      }),
      mapToVoid(),
      take(1),
    );
  }
}
